/**
 * useContentOverrides — the database layer of the content system.
 *
 * The app ships complete locale bundles (src/locales/*.json). This provider
 * fetches the admin's overrides from `public.content_translations` and exposes
 * them, plus the country-derived tokens, to `useTranslation()`:
 *
 *     override(locale, key) -> bundle(locale, key) -> bundle('en-UG', key) -> key
 *
 * Deliberate properties:
 *
 *  * **No provider is a valid state.** `useContentOverrides()` returns shipped
 *    defaults when the context is absent instead of throwing, so any component
 *    calling `t()` still renders in a unit test, a Storybook story, or an error
 *    boundary mounted above the provider. `useLanguage()` throws by design —
 *    a missing language context is a wiring bug; a missing override context is
 *    just "no overrides".
 *  * **Failure is invisible.** A network error, an RLS change, a missing table
 *    — all of them resolve to "no overrides", i.e. the copy that shipped in the
 *    bundle. Copy editing must never be able to blank the UI.
 *  * **One query for every locale.** The row set is small (only overridden
 *    strings) and switching language must not trigger a fetch, so the whole
 *    table is read once and grouped client-side.
 */
import React, { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUserCountryConfig } from '@/lib/countryConfig';
import {
  buildContentTokens,
  DEFAULT_CONTENT_TOKENS,
  type ContentTokens,
} from '@/lib/contentTokens';

/** locale -> key -> value. Only keys an admin has overridden appear here. */
export type ContentOverrides = Record<string, Record<string, string>>;

interface ContentOverridesContextValue {
  overrides: ContentOverrides;
  tokens: ContentTokens;
  isLoading: boolean;
}

const EMPTY_OVERRIDES: ContentOverrides = {};

const FALLBACK: ContentOverridesContextValue = {
  overrides: EMPTY_OVERRIDES,
  tokens: DEFAULT_CONTENT_TOKENS,
  isLoading: false,
};

const ContentOverridesContext = createContext<ContentOverridesContextValue | undefined>(undefined);

ContentOverridesContext.displayName = 'ContentOverridesContext';

/** Shared react-query key so the admin editor can invalidate published copy. */
export const CONTENT_OVERRIDES_QUERY_KEY = ['content-translations', 'published'] as const;

// eslint-disable-next-line react-refresh/only-export-components -- co-located with its provider, matching useLanguage.tsx
export function useContentOverridesQuery() {
  return useQuery({
    queryKey: CONTENT_OVERRIDES_QUERY_KEY,
    queryFn: async (): Promise<ContentOverrides> => {
      const { data, error } = await supabase
        .from('content_translations')
        .select('locale, translation_key, value')
        .eq('is_published', true);

      if (error) {
        // Shipped copy is the fallback — surface the failure without breaking
        // rendering. (No toast: an anonymous visitor cannot act on this.)
        console.error('Error fetching content translations:', error);
        return EMPTY_OVERRIDES;
      }

      const grouped: ContentOverrides = {};
      for (const row of data ?? []) {
        (grouped[row.locale] ??= {})[row.translation_key] = row.value;
      }
      return grouped;
    },
    staleTime: 1000 * 60 * 5,
    retry: 1,
    meta: { errorMessage: 'Failed to load content translations' },
  });
}

export function ContentOverridesProvider({ children }: { children: React.ReactNode }) {
  const { data, isLoading } = useContentOverridesQuery();
  // Cross-country users must see their own country in support details and
  // examples, rather than the platform's primary market.
  const { config, cities } = useUserCountryConfig();
  // Both `config` and `cities` are rebuilt on every render of the region hook,
  // so memoising on their identity would memoise nothing — and this context
  // value feeds every component that calls t(). Key on the contents instead.
  const cityKey = cities.join('|');
  const tokens = useMemo(
    () => buildContentTokens(config, cityKey ? cityKey.split('|') : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on content, not identity
    [config.code, config.name, config.currencyCode, config.support.phone,
     config.support.whatsapp, config.support.location, cityKey],
  );

  const value = useMemo<ContentOverridesContextValue>(
    () => ({ overrides: data ?? EMPTY_OVERRIDES, tokens, isLoading }),
    [data, tokens, isLoading],
  );

  return (
    <ContentOverridesContext.Provider value={value}>
      {children}
    </ContentOverridesContext.Provider>
  );
}

ContentOverridesProvider.displayName = 'ContentOverridesProvider';

/** Overrides + tokens; shipped defaults when no provider is mounted. */
// eslint-disable-next-line react-refresh/only-export-components -- co-located context hook, matching useLanguage.tsx
export function useContentOverrides(): ContentOverridesContextValue {
  return useContext(ContentOverridesContext) ?? FALLBACK;
}
